import React from 'react';

import moment from 'moment';
import classNames from 'classnames';

import Tooltip from 'rc-tooltip';

import type { MenuItem } from 'primereact/menuitem';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import {
  faExclamationCircle,
  faExclamationTriangle,
  faHeadset,
  faInfoCircle,
  faTrash,
} from '@fortawesome/pro-light-svg-icons';

import { FlexBox } from '@eltoro-ui/components';

import CustomIcons from 'assets/icons';
import OrderlineStatusAccessor from 'Pages/CampaignView/components/OrderlinesTable/components/OrderlineStatusAccessor';
import ToggleMenu from 'Pages/Orderlines/ToggleMenu';

import { CampaignStatusEnum, CampaignStep, OrderlineStatusEnum } from 'enums';
import type { TCampaign, TOrderline } from 'types';
import { formatFromUTC } from 'Utils/helpers';
import { ColumnDef } from '@tanstack/react-table';

export const getInfoIconAndTooltip = (campaign: TCampaign) => {
  if (
    campaign.status === CampaignStatusEnum.ERROR ||
    campaign.status === CampaignStatusEnum.PAYMENT_FAILED
  ) {
    return (
      <Tooltip
        placement="topRight"
        trigger="hover"
        classNames={{
          root: classNames('audience-tooltip', {
            'campaign-status-error': campaign.step !== CampaignStep.DONE,
          }),
        }}
        showArrow
        align={{
          offset: [5, -3],
          targetOffset: [-6, 0],
        }}
        overlay={
          campaign.step === CampaignStep.DONE
            ? 'Campaign creation has failed.'
            : 'There are errors in your campaign. Please review them before publishing.'
        }
        getTooltipContainer={() => document.body}
      >
        <span style={{ cursor: 'pointer', display: 'flex' }}>
          <FontAwesomeIcon icon={faExclamationCircle} color="#FF0000" style={{ fontSize: 20 }} />
        </span>
      </Tooltip>
    );
  }
  return null;
};

export const getOderlineWarningIcon = (orderline: TOrderline) => {
  const showTooltipIfPaused =
    orderline.status === OrderlineStatusEnum.PAUSED &&
    moment(orderline.end_time).isBefore(moment());
  if (orderline.status === OrderlineStatusEnum.REJECTED || showTooltipIfPaused) {
    return (
      <Tooltip
        placement="topRight"
        trigger="hover"
        classNames={{ root: 'audience-tooltip' }}
        showArrow
        align={{
          offset: [5, -3],
          targetOffset: [-6, 0],
        }}
        overlay={
          orderline.status === OrderlineStatusEnum.REJECTED ? (
            <p style={{ width: '285px' }}>
              Your ad was rejected.
              <br />
              Go to "View Details" to check the rejection note for details and make the needed
              updates.
            </p>
          ) : (
            <p style={{ width: '200px' }}>
              You orderline end date is passed,
              <br /> Please contact support at help@beewo.com for assistance.
            </p>
          )
        }
        getTooltipContainer={() => document.body}
      >
        <span style={{ cursor: 'pointer', display: 'flex' }}>
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            style={{ fontSize: 18, color: '#FFAB03' }}
          />
        </span>
      </Tooltip>
    );
  }
  return null;
};
export const redirectCampaign = (campaign: TCampaign) => {
  if (campaign.step === CampaignStep.AUDIENCE) {
    return `/create-campaign/${campaign?.id}/setup`;
  }
  if (campaign.step === CampaignStep.CREATIVE) {
    return `/create-campaign/${campaign?.id}/creatives`;
  }
  if (campaign.step === CampaignStep.CHECKOUT) {
    return `/create-campaign/${campaign?.id}/calculate`;
  }

  return `/campaigns/${campaign.id}`;
};
export const redirectOrderline = (orderline: TOrderline) => {
  return `/orderlines/${orderline.orderline_uuid}`;
};
export const campaignActionItems = (
  campaign: TCampaign,
  userIsActive: boolean,
  onDelete: (campaign: TCampaign) => void,
  changeRoutePath: (campaign: TCampaign) => void
): MenuItem[] => {
  const edit = {
    label: 'Continue editing',
    icon: <CustomIcons name="edit" color="#222124" fontSize={12} />,
    command: () => (userIsActive ? changeRoutePath(campaign) : {}),
  };

  const allowEdit =
    (campaign.step !== CampaignStep.DONE &&
      (campaign.status === CampaignStatusEnum.CREATING ||
        campaign.status === CampaignStatusEnum.ERROR)) ||
    (campaign.status === CampaignStatusEnum.PAYMENT_FAILED &&
      campaign.step === CampaignStep.CHECKOUT);

  return [
    ...(allowEdit ? [edit] : []),
    {
      label: 'Delete',
      className: 'delete',
      icon: <FontAwesomeIcon icon={faTrash} color="#ED6762" style={{ fontSize: 12 }} />,
      command: () => onDelete(campaign),
    },
  ];
};

export const orderlineActionItems = (
  orderline: TOrderline,
  handleOpenRenameModal: (open: boolean, orderline: TOrderline) => void,
  changeRoutePath: (orderline: TOrderline) => void
): MenuItem[] => {
  const rename = {
    label: 'Rename',
    icon: <CustomIcons name="edit" color="#222124" fontSize={12} />,
    command: () => handleOpenRenameModal(true, orderline),
  };
  const viewDetails = {
    label: 'View details',
    icon: <FontAwesomeIcon icon={faInfoCircle} name="details" color="#222124" fontSize={12} />,
    command: () => changeRoutePath(orderline),
  };
  const contactSupport = {
    label: 'Contact Support',
    url: '/profile/notifications',
    target: '_blank',
    icon: <FontAwesomeIcon icon={faHeadset} color="#222124" fontSize={12} name="support" />,
  };

  if (
    orderline.status === OrderlineStatusEnum.PAUSED &&
    moment(orderline.end_time).isBefore(moment())
  ) {
    return [rename, viewDetails, contactSupport];
  }
  if (orderline.status === OrderlineStatusEnum.COMPLETED) {
    return [viewDetails];
  }
  return [rename, viewDetails];
};

export const getCampaignActions = (
  campaign: TCampaign,
  userIsActive: boolean,
  onDelete: (campaign: TCampaign) => void,
  changeRoutePath: (campaign: TCampaign) => void
) => {
  if (campaign.status === CampaignStatusEnum.CREATING) {
    if (campaign.step !== CampaignStep.DONE)
      return (
        <ToggleMenu
          menuItems={campaignActionItems(campaign, userIsActive, onDelete, changeRoutePath)}
          isButtonDisable={false}
        />
      );

    return (
      <FontAwesomeIcon
        icon={faSpinnerThird}
        color="#FFAB03"
        style={{ fontSize: 20, animation: 'spinner-border 0.75s linear infinite' }}
      />
    );
  }
  if (
    (campaign.status === CampaignStatusEnum.ERROR && campaign.step === CampaignStep.DONE) ||
    (campaign.status === CampaignStatusEnum.PAYMENT_FAILED &&
      campaign.step === CampaignStep.CHECKOUT)
  ) {
    return (
      <ToggleMenu
        menuItems={campaignActionItems(campaign, userIsActive, onDelete, changeRoutePath)}
        isButtonDisable={false}
      />
    );
  }
  if (campaign.status === CampaignStatusEnum.ERROR && campaign.step !== CampaignStep.DONE) {
    return (
      <ToggleMenu
        menuItems={campaignActionItems(campaign, userIsActive, onDelete, changeRoutePath)}
        isButtonDisable={false}
      />
    );
  }
  return (
    <ToggleMenu
      menuItems={campaignActionItems(campaign, userIsActive, onDelete, changeRoutePath)}
      isButtonDisable
    />
  );
};

export const getOrderlineActions = (
  orderline: TOrderline,
  userIsActive: boolean,
  handleOpenRenameModal: (open: boolean, orderline: TOrderline) => void,
  changeRoutePath: (orderline: TOrderline) => void
) => {
  return (
    <ToggleMenu
      menuItems={orderlineActionItems(orderline, handleOpenRenameModal, changeRoutePath)}
      isButtonDisable={!userIsActive}
    />
  );
};

export const onViewDetailCampaign = (
  campaign: TCampaign,
  userIsActive: boolean,
  onDelete: (campaign: TCampaign) => void,
  changeRoutePath: (campaign: TCampaign) => void
) => {
  return (
    <FlexBox
      alignItems="center"
      justifyContent="flex-end"
      gap="12px"
      UNSAFE_style={{ marginInlineEnd: '8px' }}
    >
      {getInfoIconAndTooltip(campaign)}
      {getCampaignActions(campaign, userIsActive, onDelete, changeRoutePath)}
    </FlexBox>
  );
};

export const orderlineAction = (
  orderline: TOrderline,
  userIsActive: boolean,
  handleOpenRenameModal: (open: boolean, orderline: TOrderline) => void,
  changeRoutePath: (orderline: TOrderline) => void
) => {
  return (
    <FlexBox alignItems="center" justifyContent="flex-end" gap="12px">
      {getOderlineWarningIcon(orderline)}
      {getOrderlineActions(orderline, userIsActive, handleOpenRenameModal, changeRoutePath)}
    </FlexBox>
  );
};

export const getCampaignColumns = (
  userIsActive: boolean,
  changeRoutePath: (campaign: TCampaign) => void,
  onDelete: (campaign: TCampaign) => void
): ColumnDef<TCampaign>[] => [
  {
    header: 'Campaign Name',
    accessorKey: 'name',
    cell: ({ row: { original: campaign } }) => (
      <FlexBox
        flexDirection="column"
        gap={
          campaign.status === CampaignStatusEnum.CREATING && campaign.step !== CampaignStep.DONE
            ? '2px'
            : '0'
        }
      >
        <span className="campaign-name">{campaign.name}</span>
        {campaign.status === CampaignStatusEnum.CREATING && campaign.step !== CampaignStep.DONE && (
          <span className="campaign-status">Draft</span>
        )}
      </FlexBox>
    ),
  },
  {
    header: 'Orderlines',
    accessorKey: 'orderline_count',
    cell: ({
      row: {
        original: { orderline_count },
      },
    }) => orderline_count || '-',
    sortDescFirst: false,
  },
  {
    header: 'Campaign Goal',
    accessorKey: 'goal',
    accessorFn: campaign => campaign.goal || '-',
  },
  {
    header: '',
    id: 'actions',
    size: 115,
    enableSorting: false,
    enableResizing: false,
    cell: ({ row: { original: campaign } }) =>
      onViewDetailCampaign(campaign, userIsActive, onDelete, changeRoutePath),
  },
];

export const getOrderlineColumns = (
  userIsActive: boolean,
  handleOpenRenameModal: (open: boolean, orderline: TOrderline) => void,
  changeRoutePath: (orderline: TOrderline) => void
): ColumnDef<TOrderline>[] => [
  {
    header: 'Ad Name',
    accessorKey: 'name',
    cell: ({ row: { original: orderline } }) => (
      <FlexBox flexDirection="column">
        <span className="campaign-name">{orderline.name}</span>
      </FlexBox>
    ),
  },
  {
    header: 'Campaign',
    accessorKey: 'campaign_name',
    accessorFn: orderline => orderline.campaign_name || '-',
  },
  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row: { original: orderline } }) =>
      <OrderlineStatusAccessor status={orderline.status} /> || '-',
  },
  {
    header: 'Type',
    accessorKey: 'orderline_type',
    accessorFn: orderline => orderline.orderline_type || '-',
  },
  {
    header: 'Start Date',
    accessorKey: 'start_time',
    accessorFn: orderline =>
      moment(formatFromUTC(`${orderline.start_time}Z`)).format('MM/DD/YY') || '-',
  },
  {
    header: 'End Date',
    accessorKey: 'end_time',
    accessorFn: orderline =>
      moment(formatFromUTC(`${orderline.end_time}Z`)).format('MM/DD/YY') || '-',
  },
  {
    header: '',
    id: 'actions',
    size: 105,
    enableSorting: false,
    enableResizing: false,
    cell: ({ row: { original: orderline } }) =>
      orderlineAction(orderline, userIsActive, handleOpenRenameModal, changeRoutePath),
  },
];

export const formatPriceRange = (range: string) => {
  if (!range) return null;

  const [min, max] = range.split('-');

  const format = (numStr: string) => {
    const num = parseInt(numStr, 10);
    return `$${num.toLocaleString('en-US')}`;
  };

  return `${format(min)} - ${format(max)}`;
};

export const formatSQFT = (range: string) => {
  if (!range) return null;

  const [min, max] = range.split('-');
  const format = (numStr: string) => {
    const num = parseInt(numStr, 10);
    return num === 0 ? '0' : `${num.toLocaleString('en-US')}sqft`;
  };

  return `${format(min)} - ${format(max)}`;
};
